if (!customElements.get('multicolumn-slider')) {
  customElements.define(
    'multicolumn-slider',
    class MulticolumnSlider extends HTMLElement {
      connectedCallback() {
        if (this.dataset.mobileSlider !== 'true') {
          return;
        }

        this.track = this.querySelector('[data-multicolumn-track]');
        if (!this.track) return;

        this.sectionId = this.dataset.sectionId;
        this.savedClassName = null;
        this.wrapper = null;
        this.sliderInstance = null;
        this.prepared = false;

        this.prevEl = this.querySelector('.multicolumn-slider__btn-prev');
        this.nextEl = this.querySelector('.multicolumn-slider__btn-next');
        this.paginationEl = this.querySelector('.multicolumn-slider__pagination');

        this.handleViewport = this.handleViewport.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onSectionLoad = this.onSectionLoad.bind(this);
        this.onWindowResize =
          typeof FoxTheme !== 'undefined' &&
          FoxTheme.utils &&
          typeof FoxTheme.utils.debounce === 'function'
            ? FoxTheme.utils.debounce(() => this.handleViewport(), 150)
            : () => this.handleViewport();

        const mqString =
          (FoxTheme.config && FoxTheme.config.mediaQueryMobile) || 'screen and (max-width: 767px)';
        this.mql = window.matchMedia(mqString);
        this.mql.addEventListener('change', this.handleViewport);
        document.addEventListener('matchMobile', this.handleViewport);
        document.addEventListener('unmatchMobile', this.handleViewport);
        window.addEventListener('resize', this.onWindowResize);

        if (typeof ResizeObserver !== 'undefined') {
          this._roPending = false;
          this.resizeObserver = new ResizeObserver(() => {
            if (this._roPending) return;
            this._roPending = true;
            requestAnimationFrame(() => {
              this._roPending = false;
              this.handleViewport();
            });
          });
          this.resizeObserver.observe(this);
        }

        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:section:load', this.onSectionLoad);
        }

        this.waitForFoxTheme(() => {
          this.handleViewport();
          requestAnimationFrame(() => this.handleViewport());
        });
      }

      waitForFoxTheme(done, attempts) {
        attempts = attempts || 0;
        if (window.FoxTheme && window.FoxTheme.Carousel) {
          done();
          return;
        }
        if (attempts > 120) return;
        requestAnimationFrame(() => this.waitForFoxTheme(done, attempts + 1));
      }

      disconnectedCallback() {
        this.destroySwiper();
        if (this.mql) this.mql.removeEventListener('change', this.handleViewport);
        document.removeEventListener('matchMobile', this.handleViewport);
        document.removeEventListener('unmatchMobile', this.handleViewport);
        window.removeEventListener('resize', this.onWindowResize);
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          document.removeEventListener('shopify:block:select', this.onBlockSelect);
          document.removeEventListener('shopify:section:load', this.onSectionLoad);
        }
      }

      onSectionLoad(event) {
        if (String(event.detail.sectionId) !== String(this.sectionId)) return;
        requestAnimationFrame(() => this.handleViewport());
      }

      onBlockSelect(event) {
        if (String(event.detail.sectionId) !== String(this.sectionId)) return;
        const column = event.target?.closest?.('[data-column-index]');
        if (!column || !this.sliderInstance?.slider) return;
        const index = Number(column.dataset.columnIndex);
        if (Number.isNaN(index)) return;
        const { slider } = this.sliderInstance;
        if (slider.params.loop && typeof slider.slideToLoop === 'function') {
          slider.slideToLoop(index);
        } else {
          slider.slideTo(index);
        }
      }

      isMobileViewport() {
        const mqString =
          (FoxTheme.config && FoxTheme.config.mediaQueryMobile) || 'screen and (max-width: 767px)';
        let matches = false;
        try {
          matches = window.matchMedia(mqString).matches;
        } catch (e) {
          matches = Boolean(FoxTheme.config && FoxTheme.config.mqlMobile);
        }
        return matches;
      }

      handleViewport() {
        if (this.isMobileViewport()) {
          this.initSwiper();
        } else {
          this.destroySwiper();
        }
      }

      prepareDom() {
        if (this.prepared) return;
        this.savedClassName = this.track.className;
        const slides = Array.from(this.track.children);
        if (slides.length === 0) return;

        this.wrapper = document.createElement('div');
        this.wrapper.className = 'swiper-wrapper multicolumn-slider__wrapper';

        slides.forEach((slide) => {
          slide.classList.add('swiper-slide');
          this.wrapper.appendChild(slide);
        });

        this.track.appendChild(this.wrapper);
        this.track.className =
          'swiper multicolumn-slider__swiper multicolumn-slider__swiper--mobile multicolumn__items';
        this.prepared = true;
      }

      restoreDom() {
        if (!this.prepared || !this.wrapper) return;
        const slides = Array.from(this.wrapper.children);
        slides.forEach((slide) => {
          slide.classList.remove('swiper-slide');
          this.track.appendChild(slide);
        });
        this.wrapper.remove();
        this.wrapper = null;
        this.track.className = this.savedClassName || this.track.className;
        this.savedClassName = null;
        this.prepared = false;
      }

      initSwiper() {
        if (this.sliderInstance) return;

        const blockCount = parseInt(this.dataset.blocksCount, 10) || 0;
        if (blockCount === 0) return;

        this.prepareDom();
        if (!this.prepared) return;

        const columnGap = FoxTheme.utils.getGridColumnGap(this.track);
        const spaceBetween = typeof columnGap.mobile === 'number' ? columnGap.mobile : 16;

        const nav = this.dataset.sliderNav || 'dots';
        const scroll = this.dataset.sliderScroll || 'manual';
        const autoplayMs = parseInt(this.dataset.autoplayMs, 10) || 5000;

        const showDots = (nav === 'dots' || nav === 'both') && blockCount > 1;
        const showArrows = (nav === 'arrows' || nav === 'both') && blockCount > 1;

        const options = {
          direction: 'horizontal',
          slidesPerView: 1,
          spaceBetween,
          loop: blockCount > 1,
          grabCursor: blockCount > 1,
          allowTouchMove: blockCount > 1,
          threshold: 2,
          watchSlidesProgress: true,
          observer: true,
          observeParents: true,
          on: {
            init(swiper) {
              requestAnimationFrame(() => {
                swiper.update();
                if (swiper.navigation && typeof swiper.navigation.update === 'function') {
                  swiper.navigation.update();
                }
                if (swiper.pagination && typeof swiper.pagination.update === 'function') {
                  swiper.pagination.update();
                }
              });
            },
          },
        };

        if (showDots && this.paginationEl) {
          options.pagination = {
            el: this.paginationEl,
            clickable: true,
          };
        } else {
          options.pagination = false;
        }

        if (showArrows && this.prevEl && this.nextEl) {
          options.navigation = {
            prevEl: this.prevEl,
            nextEl: this.nextEl,
          };
        } else {
          options.navigation = false;
        }

        const modules = [];
        if (scroll === 'auto' && blockCount > 1) {
          modules.push(FoxTheme.Swiper.Autoplay);
          options.autoplay = {
            delay: autoplayMs,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          };
        }

        const extraModules = modules.length ? modules : null;
        try {
          this.sliderInstance = new window.FoxTheme.Carousel(this.track, options, extraModules);
          this.sliderInstance.init();
        } catch (e) {
          this.sliderInstance = null;
          this.restoreDom();
        }
      }

      destroySwiper() {
        if (this.sliderInstance) {
          try {
            this.sliderInstance.slider.destroy(false, false);
          } catch (e) {
            /* noop */
          }
          this.sliderInstance = null;
        }
        this.restoreDom();
      }
    }
  );
}
